import { API } from "apis";
import XButton from "components/common/button/XButton";
import PageTitle from "components/common/PageTitle";
import ConfirmDialog from "components/dialog/ConfirmDIalog";
import CorpSelect from "components/list/CorpSelect";
import { usePrompt } from "hooks/usePrompt";
import { ContextModel, CorpModel, LimitedListItemModel } from "models";
import React from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { Utils } from "utils";

const LimitedListDetailPage: React.FC = () => {
  const params = useParams();
  const navigate = useNavigate();

  const ctx = useOutletContext<ContextModel>();
  const didEffect = React.useRef(false);

  const data = React.useRef<LimitedListItemModel>();

  const [corps, setCorps] = React.useState<CorpModel[]>([]);
  const [name, setName] = React.useState("");
  const [keyword, setKeyword] = React.useState("");
  const [corpList, setCorpList] = React.useState<CorpModel[]>([]);
  const [dispCorpList, setDispCorpList] = React.useState<CorpModel[] | null>(
    null
  );

  const [formIsDirty, setFormIsDirty] = React.useState(true);
  const onBeforeUnload = (e: BeforeUnloadEvent) => {
    e.preventDefault();
    e.returnValue = "";
  };
  usePrompt("入力中の内容は破棄されます。よろしいですか？", formIsDirty);

  const [deleteMessage, setDeleteMessage] = React.useState("");

  const onAddCorpToList = (corpID: string) => {
    const target = corps.find((v) => v.corp_id === corpID);
    console.log(target);
    if (!target) {
      return false;
    }
    corpList.push(target);
    setCorpList([...corpList]);
    setKeyword("");
    setDispCorpList(null);
    return true;
  };
  const onRemoveCorpFromList = (corpID: string) => {
    const idx = corpList.findIndex((v) => v.corp_id === corpID);
    if (idx < 0) {
      return;
    }
    corpList.splice(idx, 1);
    setCorpList([...corpList]);
    setKeyword("");
    setDispCorpList(null);
  };
  const onFilterForList = () => {
    if (keyword) {
      const tmp = corpList.filter((v) => {
        return v.corp_name.includes(keyword);
      });
      setDispCorpList(tmp);
    } else {
      setDispCorpList(null);
    }
  };
  const onCreate = async () => {
    ctx.loading.setState(true);
    setFormIsDirty(false);
    try {
      const response = await API.LimitedList.create(name, corpList);
      console.log(response);
      navigate("/limited-disclosure-list/");
    } catch (err) {
      setFormIsDirty(true);
      Utils.errorAction(ctx, err);
    }
    ctx.loading.setState(false);
  };
  const onUpdate = async () => {
    if (!data.current) {
      return;
    }
    ctx.loading.setState(true);
    setFormIsDirty(false);
    try {
      const id = data.current.limited_list_id;
      const response = await API.LimitedList.update(id, name, corpList);
      console.log(response);
      navigate("/limited-disclosure-list/");
    } catch (err) {
      setFormIsDirty(true);
      Utils.errorAction(ctx, err);
    }
    ctx.loading.setState(false);
  };
  const onDelete = async () => {
    const listName = name === "" ? "限定リスト" : name;
    setDeleteMessage(`${listName}を削除します。よろしいですか？`);
  };
  const onDeleteConfirmed = async (result: boolean) => {
    setDeleteMessage("");

    if (!result) {
      return;
    }
    if (!data.current) {
      return;
    }
    ctx.loading.setState(true);
    setFormIsDirty(false);
    try {
      const id = data.current.limited_list_id;
      const response = await API.LimitedList.del(id);
      console.log(response);
      navigate("/limited-disclosure-list");
    } catch (err) {
      setFormIsDirty(true);
      Utils.errorAction(ctx, err);
    }
    ctx.loading.setState(false);
  };

  React.useEffect(() => {
    if (didEffect.current) {
      return;
    }
    didEffect.current = true;

    const f = async () => {
      ctx.loading.setState(true);
      await ctx.auth.initialize();
      await ctx.limitPrice.load();

      try {
        let corpList: CorpModel[] = [];
        if (params.id) {
          const response = await API.LimitedList.detail(params.id);
          data.current = response.data;
          setName(data.current.limited_list_name);
          corpList = data.current.limited_corp_list.map((v) => {
            return {
              corp_id: v.limited_corp_id,
              corp_name: v.limited_corp_name,
            };
          });
          setCorpList(corpList);
        }
        const responseList = await API.Corp.list();
        const corps = responseList.data;
        setCorps(corps);
      } catch (err) {
        Utils.errorAction(ctx, err);
      }
      ctx.loading.setState(false);
    };
    f();
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <Helmet>
        <title>限定公開リスト詳細</title>
      </Helmet>
      <PageTitle title="限定公開リスト詳細" className="p-2" />

      <div className="m-4">
        <div className="mt-8 mb-4 flex items-center gap-4">
          <div>
            リスト名称<span className="ml-2 text-red-500 font-bold">*</span>
          </div>
          <div className="flex-1">
            <input
              className="w-full"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
            />
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div>
            会社を設定<span className="ml-2 text-red-500 font-bold invisible">*</span>
          </div>
          <div className="flex-1">
            <CorpSelect
              corps={corps}
              list={corpList}
              onSelect={onAddCorpToList}
              disabled={corpList.length >= 100}
            />
          </div>
        </div>
        <div className="mt-16 p-4 border border-gray-400 rounded">
          <div>設定中の会社</div>

          <div className="mt-4 flex items-center gap-4">
            <div className="flex-1">
              <input
                className="w-full"
                placeholder="キーワードを入力してください"
                value={keyword}
                onChange={(e) => {
                  setKeyword(e.target.value);
                }}
              />
            </div>
            <div>
              <button className="primary px-4 py-2" onClick={onFilterForList}>
                検索
              </button>
            </div>
          </div>

          <div className="my-4 flex justify-end">
            <span>{corpList.length}/100</span>
          </div>

          <div className="border border-black p-4 min-h-[20rem] max-h-[40rem] overflow-y-scroll">
            {(dispCorpList ? dispCorpList : corpList).map((item, index) => {
              return (
                <div
                  className="border-b border-b-gray-300 p-2 flex justify-between items-center"
                  key={index}
                >
                  <span>{item.corp_name}</span>
                  <XButton
                    action={() => {
                      onRemoveCorpFromList(item.corp_id);
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {data.current ? (
          <div className="mt-8 mb-2 flex justify-center items-center gap-8">
            <button className="primary w-24 py-2" onClick={onDelete}>
              削除
            </button>
            <button className="primary w-24 py-2" onClick={onUpdate}>
              更新
            </button>
          </div>
        ) : (
          <div className="mt-8 mb-2 flex justify-center items-center gap-8">
            <button className="primary w-24 py-2" onClick={onCreate}>
              登録
            </button>
          </div>
        )}
      </div>
      <ConfirmDialog
        modalIsOpen={!!deleteMessage}
        message={deleteMessage}
        action={onDeleteConfirmed}
      />
    </div>
  );
};
export default LimitedListDetailPage;
